// Pure season/standings engine for the isolated G-Season sandbox. Accumulates
// by-place points across events into a running leaderboard. COMPOSITION: reuses
// buildReports (→ rankTeams) for each event's finish; season logic sits on top.
// No React, no Firebase, no DB — unit-testable under `node --test`.
import { buildReports } from './ggReports.js'

export const DEFAULT_POINTS = [100, 80, 60, 50, 45, 40, 36, 32, 29, 26] // by place (index 0 = 1st)

const csv = (s) => (s == null ? '' : `"${String(s).replace(/"/g, '""')}"`)
const pointsForPlace = (place, scheme) => Number(scheme?.[place - 1]) || 0

// One event's finish, reusing the ranking engine. score = vs-par (for ties).
export function eventFinish(event, { net = false } = {}) {
  const reports = buildReports(event?.tournament || {})
  const rows = net ? reports.overall.net : reports.overall.gross
  return rows.map((r) => ({ teamId: r.teamId, teamName: r.teamName, place: r.place, score: r.vsPar }))
}

// Points per team for one event, with tie-splitting: teams sharing the same
// score (adjacent in the ranking) each get the AVERAGE of their positions'
// scheme points.
function eventPoints(finish, scheme) {
  const map = {}
  let i = 0
  while (i < finish.length) {
    let j = i
    while (j + 1 < finish.length && finish[j + 1].score === finish[i].score) j++
    const group = finish.slice(i, j + 1)
    const avg = group.reduce((s, g) => s + pointsForPlace(g.place, scheme), 0) / group.length
    for (const g of group) map[g.teamId] = avg
    i = j + 1
  }
  return map
}

export function seasonStandings(season, { scheme = DEFAULT_POINTS, dropWorst = 0, net = false, participation = 0, keyBy = 'id' } = {}) {
  // keyBy 'id' = sandbox default (teamIds stable across the sample season).
  // keyBy 'name' = LIVE Order-of-Merit: real events have different teamIds per
  // event, so accumulate by normalized team name to link a team across events.
  const keyFor = (f) => (keyBy === 'name' ? String(f.teamName || f.teamId).trim().toLowerCase() : f.teamId)
  const events = (season?.events || []).map((e) => ({ id: e.id, name: e.name }))
  const teamNames = {}
  const perEventPts = {} // eventId → { key → { place, points } }
  for (const e of season?.events || []) {
    const finish = eventFinish(e, { net })
    const pts = eventPoints(finish, scheme)
    perEventPts[e.id] = {}
    for (const f of finish) {
      const k = keyFor(f)
      teamNames[k] = f.teamName
      perEventPts[e.id][k] = { place: f.place, points: pts[f.teamId] }
    }
  }

  const rows = Object.keys(teamNames).map((teamId) => {
    const perEvent = events.map((e) => {
      const p = perEventPts[e.id][teamId]
      return { eventId: e.id, place: p ? p.place : null, points: p ? p.points : 0, dropped: false }
    })
    if (dropWorst > 0) {
      const order = [...perEvent].sort((a, b) => a.points - b.points)
      for (let k = 0; k < Math.min(dropWorst, order.length); k++) order[k].dropped = true
    }
    const counted = perEvent.filter((pe) => !pe.dropped)
    const total = counted.reduce((s, pe) => s + pe.points, 0) + (Number(participation) || 0) * counted.length
    return { teamId, teamName: teamNames[teamId], perEvent, total, countedEvents: counted.length }
  })

  const firsts = (row) => row.perEvent.filter((pe) => pe.place === 1).length
  rows.sort((a, b) => b.total - a.total || firsts(b) - firsts(a))
  return { events, rows }
}

export function seasonCsv(standings) {
  const events = standings?.events || []
  const lines = [['Rank', 'Team', ...events.map((e) => csv(e.name)), 'Total'].join(',')]
  ;(standings?.rows || []).forEach((r, i) => {
    lines.push([i + 1, csv(r.teamName), ...r.perEvent.map((pe) => (pe.dropped ? `(${pe.points})` : pe.points)), r.total].join(','))
  })
  return lines.join('\n')
}
